import React from "react";
import { AlertTriangle, Info } from "lucide-react";
import type { GateState } from "./billingProfileGate";

interface BillingProfileGateBannerProps {
  state: GateState;
  canSetup: boolean;
  onSetup: () => void;
  /** Distinguishes the same banner across the surfaces it can co-render on
   *  (builder step, order-details drawer, payment drawer), so cypress ids stay
   *  unique when more than one is in the DOM at once. */
  surface: string;
  /** 'checkout' swaps the informational copy for the payment-drawer wording (AC200). */
  variant?: "builder" | "checkout";
}

/**
 * The billing-profile gate banner. Two looks:
 *  - informational (Standard/EDDM-lite, no profile): soft blue, nothing blocked.
 *  - required (Enterprise/Agency, no profile): amber warning; the action shows
 *    only for a viewer who may act (Org Admin, or Team Admin of a single-team
 *    org), otherwise the copy tells the rest to ask their organization's administrator.
 */
export const BillingProfileGateBanner = ({
  state,
  canSetup,
  onSetup,
  surface,
  variant = "builder",
}: BillingProfileGateBannerProps) => {
  if (state === "none") return null;

  if (state === "informational") {
    return (
      <div
        data-cypress-id={`billing-profile-info-banner-${surface}`}
        cypress-id={`billing-profile-info-banner-${surface}`}
        className="flex items-start justify-between gap-4 rounded-[10px] border border-solid border-[#b2ddff] bg-[#eff6ff] px-4 py-3 dark:border-[#1e3a5f] dark:bg-[#0b213f]"
      >
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#1447e6] dark:text-[#8ec5ff]" />
          {variant === "checkout" ? (
            <span className="text-sm font-medium leading-5 text-[#193cb8] dark:text-[#dbeafe]">
              Invoices will use your account details unless you add a billing profile.
            </span>
          ) : (
            <div className="flex flex-col gap-1 text-sm leading-5">
              <span className="font-medium text-[#193cb8] dark:text-[#dbeafe]">
                Invoices will use your account details by default.
              </span>
              <span className="text-[#1447e6] dark:text-[#bedbff]">
                Add a billing profile to change the company name or address on them.
              </span>
            </div>
          )}
        </div>
        {canSetup && <SetupAction onSetup={onSetup} tone="info" surface={surface} />}
      </div>
    );
  }

  // state === "required"
  return (
    <div
      data-cypress-id={`billing-profile-required-banner-${surface}`}
      cypress-id={`billing-profile-required-banner-${surface}`}
      className="flex items-start justify-between gap-4 rounded-[10px] border border-solid border-[#fee685] bg-[#fffbeb] px-4 py-3 dark:border-[#973c00] dark:bg-[#461901]"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#7b3306] dark:text-[#fef3c6]" />
        {canSetup ? (
          <div className="flex flex-col gap-1 text-sm leading-5">
            <span className="font-medium text-[#7b3306] dark:text-[#fef3c6]">
              A billing profile is required to launch campaigns
            </span>
            <span className="text-[#973c00] dark:text-[#fef3c6]">
              Enter the company name, address and contacts for your invoices once. Every invoice after that uses them
              automatically.
            </span>
          </div>
        ) : (
          <span className="text-sm font-medium leading-5 text-[#7b3306] dark:text-[#fef3c6]">
            A billing profile is required to launch campaigns. Ask your organization's administrator to set one up.
          </span>
        )}
      </div>
      {canSetup && <SetupAction onSetup={onSetup} tone="warning" surface={surface} />}
    </div>
  );
};

const SetupAction = ({
  onSetup,
  tone,
  surface,
}: {
  onSetup: () => void;
  tone: "info" | "warning";
  surface: string;
}) => (
  <button
    type="button"
    onClick={onSetup}
    data-cypress-id={`billing-profile-setup-action-${surface}`}
    cypress-id={`billing-profile-setup-action-${surface}`}
    className={
      tone === "info"
        ? "shrink-0 whitespace-nowrap bg-transparent p-0 text-sm font-medium text-[#1447e6] underline dark:text-[#8ec5ff]"
        : "shrink-0 whitespace-nowrap bg-transparent p-0 text-sm font-medium text-[#973c00] underline dark:text-[#fef3c6]"
    }
  >
    Set up Billing Profile
  </button>
);
